import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getProfile } from '../store/slices/authSlice';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  Button,
  Divider,
  Link,
} from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Page displayed to users with pending approval status
 * @returns {JSX.Element} The PendingApprovalPage component
 */
const PendingApprovalPage = () => {
  const { t } = useTranslation('user');
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(getProfile());
    }, 15000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // Handle rejected status
  if (user?.status === 'rejected') {
    return (
      <Container
        maxWidth="md"
        sx={{ mt: 4 }}
      >
        <Paper
          elevation={3}
          sx={{ p: 4, borderRadius: 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'error.light',
                borderRadius: '50%',
                p: 2,
                mb: 2,
              }}
            >
              <InfoIcon
                fontSize="large"
                color="error"
              />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              align="center"
            >
              {t('approval.rejectedTitle')}
            </Typography>
          </Box>

          <Alert
            severity="error"
            sx={{ mb: 3 }}
          >
            <AlertTitle>{t('approval.rejectedAlertTitle')}</AlertTitle>
            {t('approval.rejectedAlertMessage')}
          </Alert>

          <Typography
            variant="body1"
            paragraph
          >
            {t('approval.rejectedMessage')}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="subtitle1"
              gutterBottom
            >
              {t('approval.contactSupport')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EmailIcon />}
              href="mailto:support@backcode.com"
              sx={{ mt: 1 }}
            >
              {t('approval.contactButton')}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Default: pending status
  return (
    <Container
      maxWidth="md"
      sx={{ mt: 4 }}
    >
      <Paper
        elevation={3}
        sx={{ p: 4, borderRadius: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'warning.light',
              borderRadius: '50%',
              p: 2,
              mb: 2,
            }}
          >
            <HourglassEmptyIcon
              fontSize="large"
              color="warning"
            />
          </Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            align="center"
          >
            {t('approval.pendingTitle')}
          </Typography>
        </Box>

        <Alert
          severity="info"
          sx={{ mb: 3 }}
        >
          <AlertTitle>{t('approval.pendingAlertTitle')}</AlertTitle>
          {t('approval.pendingAlertMessage')}
        </Alert>

        <Typography
          variant="body1"
          paragraph
        >
          {t('approval.welcomeMessage', { company: user?.companyName })}
        </Typography>

        <Typography
          variant="body1"
          paragraph
        >
          {t('approval.pendingMessage')}
        </Typography>

        <Typography
          variant="body1"
          paragraph
        >
          {t('approval.reviewProcess')}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="subtitle1"
            gutterBottom
          >
            {t('approval.questions')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            href="mailto:support@backcode.com"
            sx={{ mt: 1 }}
          >
            {t('approval.contactButton')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PendingApprovalPage;
